import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { auth } from '../../config/firebase';
import useChangeRadarStore, { type ChangeRadarTicket } from '../../store/changeRadarStore';
import { isPremiumCostRadarTicket } from '../../types/changeRadar';
import { formatMandantNrDisplay } from '../../utils/mandantNrFormat';

const IconBase = ({ children, size = 20, className = '' }: any) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {children}
    </svg>
);

const XIcon = (props: any) => (
    <IconBase {...props}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </IconBase>
);

const CheckIcon = (props: any) => (
    <IconBase {...props}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <path d="m9 11 3 3L22 4" />
    </IconBase>
);

const TrashIcon = (props: any) => (
    <IconBase {...props}>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
    </IconBase>
);

const formatAt = (raw: unknown): string => {
    let ms = 0;
    if (typeof raw === 'number') ms = raw;
    else if (raw && typeof (raw as any).toMillis === 'function') ms = (raw as any).toMillis();
    else if (raw && typeof (raw as any).seconds === 'number') ms = (raw as any).seconds * 1000;
    else ms = Date.parse(String(raw || '')) || 0;
    if (!ms) return '—';
    try {
        return new Date(ms).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

const typeLabel = (t: ChangeRadarTicket): string => {
    if (t.category === 'Abo-Kündigung') return 'Abo-Kündigung';
    if (t.category === 'Kosten-Update' || t.toolId) return 'Softwarekosten';
    if (t.type === 'both') return 'Änderungen + HJ2';
    if (t.type === 'unlock_h2') return 'HJ2 Freischaltung';
    return 'Änderungsantrag';
};

const showGoldBadge = (t: ChangeRadarTicket) =>
    t.unlock_h2 === true || t.type === 'unlock_h2' || t.type === 'both';

export type PEChangeRadarModalProps = {
    isOpen: boolean;
    activeTab: 'offen' | 'erledigt';
    setActiveTab: (t: 'offen' | 'erledigt') => void;
    pendingRequests: ChangeRadarTicket[];
    completedRequests: ChangeRadarTicket[];
    isLoading: boolean;
    error: string | null;
    busyId: string | null;
    onClose: () => void;
};

/**
 * Isoliertes Change-Radar Modal — Noble Slate (bg-slate-900/800, border-slate-700, amber-500).
 */
const PEChangeRadarModal: React.FC<PEChangeRadarModalProps> = ({
    isOpen,
    activeTab,
    setActiveTab,
    pendingRequests,
    completedRequests,
    isLoading,
    error,
    busyId,
    onClose,
}) => {
    const deleteRequest = useChangeRadarStore((s) => s.deleteRequest);
    const completeRequest = useChangeRadarStore((s) => s.completeRequest);
    const [confirmTicket, setConfirmTicket] = useState<ChangeRadarTicket | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [completingId, setCompletingId] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleConfirmDelete = async () => {
        if (!confirmTicket) return;
        setDeleting(true);
        try {
            await deleteRequest(confirmTicket.clientId, confirmTicket.id);
            setConfirmTicket(null);
        } catch (err) {
            console.error('deleteRequest:', err);
            alert('Löschen fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
            setDeleting(false);
        }
    };

    const handleMarkDone = async (ticket: ChangeRadarTicket) => {
        if (!isPremiumCostRadarTicket(ticket)) return;
        const user = auth.currentUser;
        setCompletingId(ticket.id);
        try {
            await completeRequest(
                ticket.clientId,
                ticket.id,
                user?.uid || '',
                user?.displayName || user?.email || 'Coach'
            );
        } catch (err) {
            console.error('completeRequest:', err);
            alert('Erledigt setzen fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
            setCompletingId(null);
        }
    };

    const DeleteBtn = ({ ticket }: { ticket: ChangeRadarTicket }) => (
        <button
            type="button"
            title="Ticket löschen"
            aria-label="Ticket löschen"
            disabled={deleting || busyId === ticket.id || completingId === ticket.id}
            onClick={() => setConfirmTicket(ticket)}
            className="p-2 rounded border border-slate-600 text-rose-400 hover:bg-rose-500/10 hover:border-rose-400/50 disabled:opacity-50 transition-colors"
        >
            <TrashIcon size={16} />
        </button>
    );

    /** Nur Softwarekosten/Abo — AZ-Änderungen erledigen sich automatisch beim Sperren. */
    const ErledigtBtn = ({ ticket }: { ticket: ChangeRadarTicket }) => {
        if (!isPremiumCostRadarTicket(ticket)) return null;
        const busy = completingId === ticket.id || busyId === ticket.id;
        return (
            <button
                type="button"
                title="Als erledigt markieren"
                aria-label="Als erledigt markieren"
                disabled={busy || deleting}
                onClick={() => void handleMarkDone(ticket)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
            >
                <CheckIcon size={14} />
                {busy ? '…' : 'Erledigt'}
            </button>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in text-left">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col max-h-[85vh]">
                <div className="p-6 border-b border-slate-700 flex justify-between items-center shrink-0 bg-slate-800 rounded-t-2xl">
                    <div>
                        <div className="text-[10px] text-amber-500 font-black tracking-[0.3em] uppercase mb-1">
                            Änderungs-Zentrale Radar
                        </div>
                        <h2 className="text-xl font-bold text-white tracking-widest uppercase">
                            Change Radar
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors border border-slate-700"
                    >
                        <XIcon size={20} />
                    </button>
                </div>

                <div className="px-6 pt-4 flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setActiveTab('offen')}
                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${
                            activeTab === 'offen'
                                ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
                        }`}
                    >
                        Offen ({pendingRequests.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('erledigt')}
                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${
                            activeTab === 'erledigt'
                                ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
                        }`}
                    >
                        Erledigt ({completedRequests.length})
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-3">
                    {error && (
                        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs p-3 rounded-lg">
                            {error}
                        </div>
                    )}
                    {isLoading && (
                        <div className="text-center py-10 text-slate-500 text-xs font-bold uppercase tracking-widest animate-pulse">
                            Lade Tickets…
                        </div>
                    )}

                    {!isLoading && !error && activeTab === 'offen' && pendingRequests.length === 0 && (
                        <div className="text-center py-12">
                            <CheckIcon size={48} className="mx-auto text-emerald-600/50 mb-4" />
                            <h3 className="text-slate-300 font-bold uppercase tracking-widest">
                                Keine offenen Anträge
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                Aktuell keine pending change_requests im Radar.
                            </p>
                        </div>
                    )}

                    {!isLoading &&
                        activeTab === 'offen' &&
                        pendingRequests.map((t) => {
                            const nr =
                                formatMandantNrDisplay(t.mandantNr) || t.mandantNr || '—';
                            return (
                                <div
                                    key={`${t.clientId}_${t.id}`}
                                    className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3"
                                >
                                    <div className="flex justify-between items-start gap-3">
                                        <div>
                                            <div className="font-bold text-white text-sm">
                                                {t.firmenname || 'Unbekannter Mandant'}
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-mono mt-1">
                                                Nr: {nr}
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-1">
                                                Betreuer:{' '}
                                                <span className="text-slate-300">
                                                    {t.coachName || t.betreuer || '—'}
                                                </span>
                                            </div>
                                        </div>
                                        <span
                                            className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded border shrink-0 ${
                                                showGoldBadge(t)
                                                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/50'
                                                    : 'bg-slate-900 text-slate-400 border-slate-600'
                                            }`}
                                        >
                                            {typeLabel(t)}
                                        </span>
                                    </div>
                                    {t.message && (
                                        <p className="text-xs text-slate-300 leading-relaxed border-t border-slate-700 pt-3">
                                            {t.message}
                                        </p>
                                    )}
                                    {t.senderName && (
                                        <p className="text-[10px] text-slate-500">
                                            Von: {t.senderName}
                                            {t.senderEmail ? ` · ${t.senderEmail}` : ''}
                                        </p>
                                    )}
                                    <div className="flex justify-end items-center gap-2 pt-1">
                                        <ErledigtBtn ticket={t} />
                                        <DeleteBtn ticket={t} />
                                    </div>
                                </div>
                            );
                        })}

                    {!isLoading && !error && activeTab === 'erledigt' && completedRequests.length === 0 && (
                        <div className="text-center py-12">
                            <h3 className="text-slate-300 font-bold uppercase tracking-widest">
                                Noch keine Erledigungen
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                Abgeschlossene Tickets erscheinen hier chronologisch.
                            </p>
                        </div>
                    )}

                    {!isLoading &&
                        activeTab === 'erledigt' &&
                        completedRequests.map((t) => {
                            const nr =
                                formatMandantNrDisplay(t.mandantNr) || t.mandantNr || '—';
                            return (
                                <div
                                    key={`${t.clientId}_${t.id}_done`}
                                    className="bg-slate-800/60 border border-slate-700 rounded-xl p-4"
                                >
                                    <div className="flex justify-between items-start gap-3">
                                        <div>
                                            <div className="font-bold text-slate-200 text-sm">
                                                {t.firmenname || 'Unbekannter Mandant'}
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-mono mt-1">
                                                Nr: {nr}
                                            </div>
                                        </div>
                                        <span
                                            className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded border shrink-0 ${
                                                showGoldBadge(t)
                                                    ? 'bg-amber-500/10 text-amber-500/80 border-amber-500/30'
                                                    : 'bg-slate-900 text-slate-500 border-slate-600'
                                            }`}
                                        >
                                            {typeLabel(t)}
                                        </span>
                                    </div>
                                    <div className="mt-3 text-[10px] text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
                                        <span>
                                            Bearbeiter:{' '}
                                            <span className="text-slate-300">
                                                {t.completedByName || '—'}
                                            </span>
                                        </span>
                                        <span>{formatAt(t.completedAt)}</span>
                                    </div>
                                    <div className="flex justify-end pt-3">
                                        <DeleteBtn ticket={t} />
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            {/* Sicherheits-Bestätigung vor unwiderruflichem Löschen */}
            {confirmTicket && (
                <div className="fixed inset-0 z-[100001] bg-black/70 flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-rose-500/40 rounded-xl max-w-md w-full p-6 shadow-2xl">
                        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-3">
                            Ticket löschen?
                        </h3>
                        <p className="text-slate-300 text-sm leading-relaxed mb-2">
                            Möchtest du dieses Ticket wirklich unwiderruflich löschen?
                        </p>
                        <p className="text-[10px] text-slate-500 mb-6 font-mono">
                            {confirmTicket.firmenname || '—'} ·{' '}
                            {formatMandantNrDisplay(confirmTicket.mandantNr) ||
                                confirmTicket.mandantNr ||
                                '—'}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={() => setConfirmTicket(null)}
                                className="flex-1 px-4 py-2 rounded border border-slate-600 text-slate-300 text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={handleConfirmDelete}
                                className="flex-1 px-4 py-2 rounded bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                            >
                                {deleting ? '…' : 'Ja, löschen'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default PEChangeRadarModal;
